export const GPS_TRACK_STORAGE_KEY = 'qvenari_gps_track_v1';
export const LAST_POS_STORAGE_KEY = 'qvenari_last_pos_v1';
/** Eget spår sparas ett dygn — hur mycket som visas väljs under Spår i menyn. */
export const GPS_TRACK_RETENTION_MS = 24 * 60 * 60 * 1000;
export const GPS_TRACK_MAX_POINTS = 9000;
export const GPS_SAMPLE_MIN_DIST_M = 12;
export const GPS_SAMPLE_MIN_INTERVAL_MS = 7000;

export type GpsStoredPoint = { t: number; lat: number; lng: number };

export function haversineMetersFlat(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const c = s1 * s1 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * s2 * s2;
  return R * 2 * Math.atan2(Math.sqrt(c), Math.sqrt(1 - c));
}

export function safeLocalStorageGet(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

export function safeLocalStorageSet(key: string, value: string) {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    /* quota / privat läge */
  }
}

export function readGpsTrackPoints(): GpsStoredPoint[] {
  const raw = safeLocalStorageGet(GPS_TRACK_STORAGE_KEY);
  if (!raw) return [];
  try {
    const j = JSON.parse(raw);
    if (!j || j.v !== 1 || !Array.isArray(j.p)) return [];
    return j.p.filter(
      (x: unknown) =>
        x &&
        typeof (x as GpsStoredPoint).t === 'number' &&
        typeof (x as GpsStoredPoint).lat === 'number' &&
        typeof (x as GpsStoredPoint).lng === 'number',
    ) as GpsStoredPoint[];
  } catch {
    return [];
  }
}

export function writeGpsTrackPoints(pts: GpsStoredPoint[]) {
  safeLocalStorageSet(GPS_TRACK_STORAGE_KEY, JSON.stringify({ v: 1, p: pts }));
}

/** Sparar varje GPS-fix (nedtunnlad) så spår kan visas upp till 3 h bakåt. */
export function persistGpsTrackSample(lat: number, lng: number, nowMs: number) {
  const cutoff = nowMs - GPS_TRACK_RETENTION_MS;
  let pts = readGpsTrackPoints().filter(p => p.t >= cutoff);
  const last = pts[pts.length - 1];
  if (
    last &&
    nowMs - last.t < GPS_SAMPLE_MIN_INTERVAL_MS &&
    haversineMetersFlat(last, { lat, lng }) < GPS_SAMPLE_MIN_DIST_M
  ) {
    writeGpsTrackPoints(pts);
    return;
  }
  pts.push({ t: nowMs, lat, lng });
  if (pts.length > GPS_TRACK_MAX_POINTS) pts = pts.slice(-GPS_TRACK_MAX_POINTS);
  writeGpsTrackPoints(pts);
}

/**
 * Lägger in punkter som spelats in medan webbvyn sov (telefonen i fickan) bland de sparade.
 * Webbvyns egen GPS hinner ofta spara en färsk punkt innan bakgrundspunkterna hämtats, så de
 * sorteras in efter tid i stället för att läggas sist. Samma tid eller tät inpå en befintlig
 * punkt (under GPS_SAMPLE_MIN_INTERVAL_MS och GPS_SAMPLE_MIN_DIST_M) räknas som dubblett.
 */
export function mergeGpsTrackPoints(existing: GpsStoredPoint[], incoming: GpsStoredPoint[], nowMs: number): GpsStoredPoint[] {
  const cutoff = nowMs - GPS_TRACK_RETENTION_MS;
  const all = [...existing].filter(p => p.t >= cutoff).sort((a, b) => a.t - b.t);
  const fresh = incoming
    .filter(p => p.t >= cutoff && Number.isFinite(p.t) && Number.isFinite(p.lat) && Number.isFinite(p.lng))
    .sort((a, b) => a.t - b.t);
  for (const p of fresh) {
    let i = all.length;
    while (i > 0 && all[i - 1].t > p.t) i--;
    const near = (q: GpsStoredPoint | undefined) =>
      !!q && (q.t === p.t || (Math.abs(q.t - p.t) < GPS_SAMPLE_MIN_INTERVAL_MS && haversineMetersFlat(q, p) < GPS_SAMPLE_MIN_DIST_M));
    if (near(all[i - 1]) || near(all[i])) continue;
    all.splice(i, 0, { t: p.t, lat: p.lat, lng: p.lng });
  }
  return all.length > GPS_TRACK_MAX_POINTS ? all.slice(-GPS_TRACK_MAX_POINTS) : all;
}

/** Senast kända position (UX14), eller null. */
export function readLastKnownPosition(): { lat: number; lng: number; t: number } | null {
  try {
    const raw = safeLocalStorageGet(LAST_POS_STORAGE_KEY);
    const p = raw ? JSON.parse(raw) as { lat?: unknown; lng?: unknown; t?: unknown } : null;
    return p && typeof p.lat === 'number' && typeof p.lng === 'number' ? { lat: p.lat, lng: p.lng, t: typeof p.t === 'number' ? p.t : 0 } : null;
  } catch { return null; }
}

export function persistLastKnownPosition(lat: number, lng: number, nowMs: number) {
  safeLocalStorageSet(LAST_POS_STORAGE_KEY, JSON.stringify({ v: 1, t: nowMs, lat, lng }));
}
